/**
 * Walks a registered route polyline at a configurable speed (dev / CLI simulation).
 */
import * as geolib from 'geolib';
import { normalizeRoutePoints } from './routeNavigation';
import { haversineMeters as haversineLatLng } from './haversine';

function haversineMeters(a, b) {
  const d = haversineLatLng(a.lat, a.lng, b.lat, b.lng);
  return Number.isFinite(d) ? d : 0;
}

function bearingDeg(a, b) {
  return geolib.getRhumbLineBearing(
    { latitude: a.lat, longitude: a.lng },
    { latitude: b.lat, longitude: b.lng },
  );
}

function cumulativeDistances(polyline) {
  const cum = [0];
  for (let i = 1; i < polyline.length; i += 1) {
    cum.push(cum[i - 1] + haversineMeters(polyline[i - 1], polyline[i]));
  }
  return cum;
}

/**
 * @param {Array<{lat,lng}>} polyline
 * @param {number} distanceM
 * @returns {{ lat: number, lng: number, bearing: number, distanceAlongRouteM: number, totalLengthM: number }}
 */
export function pointAtDistanceAlongPolyline(polyline, distanceM) {
  const pts = normalizeRoutePoints(polyline);
  if (!pts.length) {
    return { lat: 0, lng: 0, bearing: 0, distanceAlongRouteM: 0, totalLengthM: 0 };
  }
  if (pts.length === 1) {
    return {
      lat: pts[0].lat,
      lng: pts[0].lng,
      bearing: 0,
      distanceAlongRouteM: 0,
      totalLengthM: 0,
    };
  }

  const cum = cumulativeDistances(pts);
  const totalLengthM = cum[cum.length - 1] || 0;
  const d = Math.max(0, Math.min(Number(distanceM) || 0, totalLengthM));

  for (let i = 0; i < pts.length - 1; i += 1) {
    const segStart = cum[i];
    const segEnd = cum[i + 1];
    if (d > segEnd && i < pts.length - 2) continue;

    const segLen = segEnd - segStart;
    const t = segLen > 0 ? (d - segStart) / segLen : 0;
    const a = pts[i];
    const b = pts[i + 1];
    const lat = a.lat + t * (b.lat - a.lat);
    const lng = a.lng + t * (b.lng - a.lng);
    return {
      lat,
      lng,
      bearing: bearingDeg(a, b),
      distanceAlongRouteM: d,
      totalLengthM,
    };
  }

  const last = pts[pts.length - 1];
  const prev = pts[pts.length - 2];
  return {
    lat: last.lat,
    lng: last.lng,
    bearing: bearingDeg(prev, last),
    distanceAlongRouteM: totalLengthM,
    totalLengthM,
  };
}

export function polylineTotalLengthM(polyline) {
  const pts = normalizeRoutePoints(polyline);
  if (pts.length < 2) return 0;
  return cumulativeDistances(pts).slice(-1)[0] || 0;
}

/**
 * @param {Array<{lat,lng}>} polyline
 * @param {{ speedKmh?: number, tickMs?: number, onTick?: Function, onComplete?: Function, loop?: boolean }} options
 */
export function createRouteNavigationSimulator(polyline, options = {}) {
  const pts = normalizeRoutePoints(polyline);
  const totalLengthM = polylineTotalLengthM(pts);
  let speedKmh = Number(options.speedKmh) > 0 ? Number(options.speedKmh) : 30;
  const tickMs = Number(options.tickMs) > 0 ? Number(options.tickMs) : 500;
  const loop = Boolean(options.loop);
  let onTick = typeof options.onTick === 'function' ? options.onTick : null;
  let onComplete = typeof options.onComplete === 'function' ? options.onComplete : null;

  let distanceM = 0;
  let playing = false;
  let timerId = null;
  let lastWallMs = null;

  function sample() {
    const p = pointAtDistanceAlongPolyline(pts, distanceM);
    const speedMs = speedKmh / 3.6;
    return {
      lat: p.lat,
      lng: p.lng,
      heading: p.bearing,
      speedMs,
      speedKmh,
      distanceAlongRouteM: p.distanceAlongRouteM,
      totalLengthM: p.totalLengthM,
      progressPct: p.totalLengthM > 0 ? (p.distanceAlongRouteM / p.totalLengthM) * 100 : 0,
    };
  }

  function emitTick() {
    if (onTick) onTick(sample());
  }

  function tick(now) {
    if (!playing || pts.length < 2) return;
    const t = now != null ? now : Date.now();
    if (lastWallMs == null) lastWallMs = t;
    const dtSec = Math.min(2, (t - lastWallMs) / 1000);
    lastWallMs = t;

    const advanceM = (speedKmh / 3.6) * dtSec;
    distanceM += advanceM;

    if (distanceM >= totalLengthM) {
      if (loop && totalLengthM > 0) {
        distanceM = distanceM % totalLengthM;
      } else {
        distanceM = totalLengthM;
        playing = false;
        emitTick();
        if (onComplete) onComplete(sample());
        stop();
        return;
      }
    }
    emitTick();
  }

  function start(fromDistanceM) {
    if (pts.length < 2) return false;
    if (fromDistanceM != null) distanceM = Math.max(0, Math.min(fromDistanceM, totalLengthM));
    playing = true;
    lastWallMs = null;
    stopTimer();
    emitTick();
    timerId = setInterval(() => tick(Date.now()), tickMs);
    return true;
  }

  function pause() {
    playing = false;
    lastWallMs = null;
    stopTimer();
  }

  function stopTimer() {
    if (timerId != null) {
      clearInterval(timerId);
      timerId = null;
    }
  }

  function stop() {
    pause();
  }

  function reset() {
    pause();
    distanceM = 0;
    emitTick();
  }

  function setSpeedKmh(kmh) {
    speedKmh = Number(kmh) > 0 ? Number(kmh) : speedKmh;
  }

  function jumpTo(distanceOrPct, isPercent) {
    if (isPercent) {
      distanceM = (totalLengthM * Math.max(0, Math.min(100, distanceOrPct))) / 100;
    } else {
      distanceM = Math.max(0, Math.min(Number(distanceOrPct) || 0, totalLengthM));
    }
    emitTick();
  }

  function destroy() {
    stopTimer();
    playing = false;
    onTick = null;
    onComplete = null;
  }

  return {
    start,
    pause,
    stop,
    reset,
    setSpeedKmh,
    jumpTo,
    sample,
    get playing() {
      return playing;
    },
    get distanceM() {
      return distanceM;
    },
    get totalLengthM() {
      return totalLengthM;
    },
    destroy,
  };
}
